/**
 * TierGate — Declarative tier-gating component.
 *
 * Renders `children` only when the current user's tier has the supplied
 * `feature` enabled in `ENTITLEMENTS_MANIFEST`. Otherwise renders `fallback`
 * (defaults to `null`).
 *
 * The lookup is a synchronous, in-memory check against the manifest via the
 * `useEntitlements` hook (which now exposes `hasFeature`). No network round
 * trip; no React Query cache layer to invalidate. The matching server-side
 * gate is `enforceCapability(feature)` in
 * `workers/src/_shared/middleware/enforce-capability.ts` — UI and worker stay
 * in sync because both read from the same manifest.
 *
 * NocoDB #4085 (parent #4060). Sibling of the existing `GatedSection`
 * paywall component, which is intentionally a different shape (paywall blur
 * + upgrade CTA). Use `TierGate` for "should this entire surface even
 * mount?" questions; use `GatedSection` for "show a blurred teaser when the
 * caller can't access this content" surfaces.
 *
 * @example
 *   <TierGate feature="feature:family_admin">
 *     <FamilyAdminControls />
 *   </TierGate>
 *
 * @example
 *   <TierGate
 *     feature="feature:practitioner_mode"
 *     fallback={<UpgradeCTA target="practitioner" />}
 *   >
 *     <PractitionerDashboard />
 *   </TierGate>
 */
import React from 'react';

import type { Feature } from '@dramadetector/shared/entitlements';

import { useEntitlements } from '../../hooks/useEntitlements';

export interface TierGateProps {
  /** Manifest feature key that gates this surface. */
  feature: Feature;
  /** Optional element to render when the feature is denied. Defaults to `null`. */
  fallback?: React.ReactNode;
  /** Children rendered only when the current tier has the feature enabled. */
  children: React.ReactNode;
}

export const TierGate: React.FC<TierGateProps> = ({
  feature,
  fallback = null,
  children,
}) => {
  const { hasFeature } = useEntitlements();
  if (!hasFeature(feature)) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
};

export default TierGate;
